/**
 * ディスクイメージを「メモリに載せずに」Emscripten の仮想 FS へ見せる仕組み。
 *
 * ## なぜ必要か
 *
 * PS1 の CD イメージは 1 枚 600MB を超えることがある。従来は
 * `FS.writeFile(path, new Uint8Array(await blob.arrayBuffer()))` としていたが、
 * これは
 *
 *   1. Blob 全体を JS ヒープへ展開し（600MB）
 *   2. MEMFS がもう 1 部コピーを持つ（さらに 600MB）
 *
 * ため、ピークでイメージサイズの 2 倍を要求する。iOS Safari の
 * WebContent プロセスはこの手前で落ちるので、大きい作品は起動しない。
 *
 * ## どう解くか
 *
 * PCSX-ReARMed の読み出しは `cdriso.c` の `cdread_normal` が
 * 「1 回 `fseeko` して 1 セクタ (2352 バイト) `fread` する」だけで、
 * ファイル全体を舐める処理はどこにも無い。つまり**必要な所だけ読めばよい**。
 *
 * ここでは MEMFS のノードを作ってから `stream_ops.read` だけを差し替え、
 * 読み出し要求が来たときに Blob の該当部分をその場で切り出して返す。
 * 常駐するのはブロックキャッシュ（既定 16MB）だけになり、
 * メモリ使用量が**ディスクサイズと無関係**になる。
 *
 * ## 制約
 *
 * Emscripten の FS API は同期なので、Blob を同期的に読む手段が要る。
 * これは `FileReaderSync` だけで、**Worker でしか使えない**。
 * したがってこの経路はコアを Worker で走らせているときにのみ有効になる。
 * メインスレッドでは呼び出し側がチャンク書き込みへフォールバックする。
 */

/** 1 ブロックの大きさ。CD の 1 セクタ (2352B) に対して十分大きく取る。 */
export const BLOCK_BYTES = 256 * 1024;

/**
 * キャッシュの既定上限。
 * 小さすぎると連続読み出しで毎回 Blob を切り直すことになり、
 * 大きすぎると「メモリに載せない」という目的自体を損なう。
 */
export const DEFAULT_CACHE_BYTES = 16 * 1024 * 1024;

/** [start, end) を同期的に読み出す関数。 */
export type SliceReader = (start: number, end: number) => Uint8Array;

/**
 * ブロック単位の LRU キャッシュ付きリーダ。
 *
 * `Map` は挿入順を保つので、参照時に delete → set し直すだけで
 * LRU になる。専用のリスト構造を持つ必要はない。
 */
export class LazyBlobReader {
  private readonly cache = new Map<number, Uint8Array>();
  private readonly maxBlocks: number;

  /** 実際に Blob を切り出した回数（キャッシュ効率の確認用） */
  sliceCount = 0;

  constructor(
    readonly size: number,
    private readonly readSlice: SliceReader,
    cacheBytes: number = DEFAULT_CACHE_BYTES,
  ) {
    this.maxBlocks = Math.max(1, Math.floor(cacheBytes / BLOCK_BYTES));
  }

  /** いまキャッシュが保持しているバイト数 */
  get cachedBytes(): number {
    let total = 0;
    for (const b of this.cache.values()) total += b.length;
    return total;
  }

  private block(index: number): Uint8Array {
    const hit = this.cache.get(index);
    if (hit) {
      // 参照したものを最後尾へ回す = LRU
      this.cache.delete(index);
      this.cache.set(index, hit);
      return hit;
    }

    const start = index * BLOCK_BYTES;
    const end = Math.min(start + BLOCK_BYTES, this.size);
    const bytes = this.readSlice(start, end);
    this.sliceCount++;

    this.cache.set(index, bytes);
    while (this.cache.size > this.maxBlocks) {
      const oldest = this.cache.keys().next();
      if (oldest.done) break;
      this.cache.delete(oldest.value);
    }
    return bytes;
  }

  /**
   * `position` から `length` バイトを `dest[destOffset..]` へ読み込む。
   * @returns 実際に読めたバイト数（EOF では要求より少なくなる）
   */
  read(dest: Uint8Array, destOffset: number, length: number, position: number): number {
    if (position < 0 || position >= this.size || length <= 0) return 0;

    const want = Math.min(length, this.size - position);
    let done = 0;
    while (done < want) {
      const at = position + done;
      const index = Math.floor(at / BLOCK_BYTES);
      const within = at - index * BLOCK_BYTES;
      const block = this.block(index);
      const take = Math.min(want - done, block.length - within);
      // ブロックが期待より短い（readSlice の実装が壊れている）場合に
      // 無限ループへ落ちないよう打ち切る
      if (take <= 0) break;
      dest.set(block.subarray(within, within + take), destOffset + done);
      done += take;
    }
    return done;
  }
}

/* -------------------------------------------------------------------------- */
/* Emscripten FS への接続                                                      */
/* -------------------------------------------------------------------------- */

/** この経路が使えるか（= Worker 上で FileReaderSync が使えるか） */
export function blobFsSupported(): boolean {
  return typeof FileReaderSync !== 'undefined';
}

/** Blob から同期スライスリーダを作る。Worker 専用。 */
export function makeSliceReader(blob: Blob): SliceReader {
  const reader = new FileReaderSync();
  return (start, end) => new Uint8Array(reader.readAsArrayBuffer(blob.slice(start, end)));
}

/** mountLazyFile が触る範囲だけを写した FS の型。 */
export interface LazyFsHost {
  writeFile(path: string, data: Uint8Array): void;
  analyzePath(path: string): { exists: boolean; object?: LazyFsNode | undefined };
}

export interface LazyFsNode {
  usedBytes?: number;
  contents?: unknown;
  stream_ops?: Record<string, unknown>;
}

/**
 * 空ファイルを作ってから、その読み出しだけを Blob 直読みへ差し替える。
 *
 * MEMFS は通常ファイルの大きさを `node.usedBytes` で答えるので、
 * ここを実サイズにしておけば `fseek(SEEK_END)` も `stat` も正しく動く。
 * `llseek` は `usedBytes` しか見ないため差し替え不要。
 *
 * @returns 差し替えたリーダ（テストと計測用）
 */
export function mountLazyFile(
  FS: LazyFsHost,
  path: string,
  size: number,
  readSlice: SliceReader,
  cacheBytes: number = DEFAULT_CACHE_BYTES,
): LazyBlobReader {
  FS.writeFile(path, new Uint8Array(0));

  const node = FS.analyzePath(path).object;
  if (!node || !node.stream_ops) {
    throw new Error(`mountLazyFile: ${path} のノードを取得できませんでした`);
  }

  const reader = new LazyBlobReader(size, readSlice, cacheBytes);

  node.usedBytes = size;
  // 実体は持たない。MEMFS の read/write は使わせないので null で構わない。
  node.contents = null;

  // 元の ops を prototype に残し、read だけを上書きする。
  // こうすると llseek や close は MEMFS のものがそのまま効く。
  const ops = Object.create(node.stream_ops) as Record<string, unknown>;

  ops.read = (
    _stream: unknown,
    buffer: Uint8Array,
    offset: number,
    length: number,
    position: number,
  ): number => reader.read(buffer, offset, length, position);

  // 書き込みと mmap は塞ぐ。特に mmap を素通しにすると、
  // 「メモリに載せない」ために作ったこの経路が黙って全読みへ戻ってしまう。
  ops.write = (): never => {
    throw new Error(`${path} は読み取り専用です（遅延読み込み中）`);
  };
  ops.mmap = (): never => {
    throw new Error(
      `${path} は mmap できません（遅延読み込み中）。` +
        'コアが mmap を要求する場合はチャンク書き込みへ切り替えてください。',
    );
  };

  node.stream_ops = ops;
  return reader;
}
